// Copyright (c) 2019 Sagacity Solutions Inc. All right reserved.

import axios from 'axios';
import moment from 'moment';
import { apiURL } from "@/app.config";

const HTTP = axios.create({
  baseURL: apiURL
//  headers: {'Content-Type': 'application/x-www-form-urlencoded'}
});

export default {
  refreshToken: async function(jwt) {
    HTTP.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
  },
  login: async function(credentials) {
    let response = await HTTP.post('auth', credentials);
    if (response.status == 200) {
      let jwt = response.data.token;
      HTTP.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
      return jwt;
    }
    return false;
  },
  logout: async function() {
    delete HTTP.defaults.headers.common['Authorization'];
    return true;
  },
  getPatients: async function(lastFetch = "") {
    let response = await HTTP.get('v1/patients/', {headers: {'X-LAST-FETCHED': lastFetch}});
    if (response.status == 200) {
      return {
        timestamp: response.headers['X-FETCH-TIMESTAMP'],
        patients: response.data
      };
    }  
    throw new Error(response.status);
  },
  storePatient: async function(patient) {
    let response = await HTTP.post('v1/patients/', patient);
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.status);
  },
  removePatient: async function(patient) {
    let response = await HTTP.delete(`v1/patients/${patient.ID}`);
    if (response.status == 200) {
      return true;
    }
    throw new Error(response.status);
  },
  getPatientOverallAdherence: async function(patientID) {
    let response = await HTTP.get(`v1/patients/${patientID}/overall-adherence`)
    if (response.status == 200) {
      return response.data;
    }
    return 0.0;
  },
  getReportHash: async function(patientID) {
    let response = await HTTP.get(`v1/patients/${patientID}/report-key`)
    if (response.status == 200) {
      return response.data;
    }
    return 0.0;
  },
  getSchedulesForPatient: async function(patientID) {
    let response = await HTTP.get(`v1/patients/${patientID}/schedules`);
    if (response.status == 200) {
      return {
        timestamp: response.headers['X-FETCH-TIMESTAMP'],
        schedules: response.data
      };
    }
    throw new Error(response.status);
  },
  storeSchedule: async function(schedule) {
    let response = await HTTP.post('v1/schedules/', schedule);
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.status);
  },
  removeSchedule: async function(schedule) {
    let response = await HTTP.delete(`v1/schedules/${schedule.ID}`);
    if (response.status == 200) {
      return true;
    }
    throw new Error(response.status);
  },
  getEventForTagID: async function(tagid) {
    if (tagid === undefined)
      throw new Error(400);

    let response = await HTTP.get(`v1/tag/${tagid}`, {
      headers: {
        'X-TIMESTAMP': moment().format()
      }
    });
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.status);
  },
  assignCard: async function(scheduleid, deviceType, cardType, code, description) {
    let endpoint = (deviceType == "mevia") ? "/assign/mevia" : "/assign/nfc";
    let response = await HTTP.post(`v1/schedules/${scheduleid}${endpoint}`,
                                   {cardtype: cardType, code: code, description: description });
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.status);
  },
  cloneSchedule: async function(schedule) {
    let response = await HTTP.post('v1/schedules/clone', schedule);
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.status);
  },
  confirmDoseIntake: async function(dose) {
    let response = await HTTP.post(`v1/tag/${dose.tagid}`, dose, {
      headers: {
        'X-TIMESTAMP': moment().format()
      }
    });
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.status);
  },
  getDoseForSchedule: async function(scheduleid) {
    let response = await HTTP.get(`v1/schedules/${scheduleid}/doses`);
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.status);
  },
  removeDose: async function(dose) {
    let response = await HTTP.delete(`v1/doses/${dose.ID}`);
    if (response.status == 200) {
      return true;
    }
    throw new Error(response.status);
  },
  createDose: async function(dose) {
    let response = await HTTP.post('v1/doses/', dose);
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.data);
  },
  updateDose: async function(dose) {
    let response = await HTTP.put(`v1/doses/${dose.ID}`, dose)
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.data);
  },
  updateMultiDoses: async function(doses) {
    let response = await HTTP.put('v1/doses/', doses);
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.data);
  },
  sendPasswordResetRequest: async function(username) {
    let response = await HTTP.post('password-reset-request', {username: username});
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.data);
  },
  resetPassword: async function(id, password) {
    let response = await HTTP.post('password-reset', {id: id, password: password});
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.data);
  },
  validateRequestID: async function(id) {
    let response = await HTTP.get(`password-reset/${id}`);
    if (response.status == 200) {
      return response.data.Success;
    }
    throw new Error(response.data);
  },
  changePassword: async function(data) {
    let response = await HTTP.post("v1/changepassword", data)
    if (response.status == 200) {
      return response.data.Success;
    }
    throw new Error(response.data)
  },
  getDeviceInfo: async function(scheduleid) {
    let response = await HTTP.get(`v1/schedules/${scheduleid}/device-info`);
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.data)
  },
  downloadPharmacistReport: async function(patientid, start, end) {
    let data = { 'start': start, 'end': end };
    let response = await HTTP.post(`v1/patients/${patientid}/pharmacist-report`, data, {responseType: 'blob'})
      
    if (response.status == 200) {
      var fileURL = window.URL.createObjectURL(new Blob([response.data], {type: 'application/pdf'}));
      var fileLink = document.createElement('a');

      fileLink.href = fileURL;
      fileLink.setAttribute('download', 'report.pdf');
      document.body.appendChild(fileLink);
      fileLink.click();
      fileLink.remove();
    }
  },
  uploadBulkPatientList: async function(data) {
    let response = await HTTP.post('v1/patients/bulkupload', data)
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.data);
  },
  fetchAnomalyList: async function() {
    let response = await HTTP.get('v1/anomaly-report')
    if (response.status == 200) {
      return response.data;
    }
    throw new Error(response.data);
  },
  sendIncentiveMessage: async function(patientid) {
    let response = await HTTP.get(`v1/patients/${patientid}/send-report`)
    return response.data;
  }
};
